import React, { useState } from 'react';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import FaqConstants from '@rental-manager/constants/ConstellationFaqConstants';
import { centsToDollarsDecimal, dollarsToCents, removeNonDigit } from '@common/paymentsUtils/currencyUtils';
import { MOVE_IN_COSTS_NOTE_MAX_LENGTH } from '@rental-manager/constants/PaymentConstants';
import { isMoveInCostsNoteLengthValid, validateProratedRentInputs } from '../validation';
import errorMessages from '@common/constants/errorMessages';
import { MOVE_IN_COSTS_PAYMENT_LIMIT_ERROR } from '@rental-manager/constants/NotificationConstants';
import userPersonas from '@common/constants/userPersonas';
import {
  AdornedInput,
  Box,
  Button,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  Paragraph,
  TextButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@zillow/constellation-10';
import Modal from '@common/components/ConstellationV10Modal';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface Props {
  saveStateToReduxStore: (e) => Promise<any>;
  editMode: boolean;
  fromDashboard: boolean;
  proratedRentPaymentAmount: number;
  proratedRentNotes: string;
  proratedRentStartsOpen: boolean;
  moveInCostsPaymentLimits: number;
  totalPaymentAmount: number;
  showModalNotification: (args: string) => Record<string, any>;
  individualLimit: number;
  proratedRentFromLease: number;
  pageVisited: Set<number>;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  onClose: () => void;
  togglePrevPage: () => void;
}

const ProratedRentPage = ({
  saveStateToReduxStore,
  editMode = false,
  fromDashboard = false,
  proratedRentPaymentAmount,
  proratedRentNotes,
  proratedRentStartsOpen,
  moveInCostsPaymentLimits,
  totalPaymentAmount: initialTotalPaymentAmount,
  showModalNotification,
  proratedRentFromLease,
  individualLimit,
  pageVisited,
  currentProperty,
  user,
  onClose,
  togglePrevPage,
}: Props) => {
  const initialIsOpen =
    !editMode && !pageVisited?.has(2) && proratedRentFromLease && proratedRentPaymentAmount === 0
      ? true
      : proratedRentStartsOpen;

  const [isOpen, setIsOpen] = useState(initialIsOpen);
  const [totalPaymentAmount, setTotalPaymentAmount] = useState(
    proratedRentPaymentAmount === 0 ? initialTotalPaymentAmount : initialTotalPaymentAmount - proratedRentPaymentAmount,
  );

  const [moveInCosts, setMoveInCosts] = useState({
    moveInCostsPaymentType: 'Prorated Rent',
    moveInCostsNotes: proratedRentNotes,
    moveInCostsPaymentAmount:
      proratedRentPaymentAmount === 0
        ? !editMode && !pageVisited?.has(2) && proratedRentFromLease
          ? String(centsToDollarsDecimal(proratedRentFromLease))
          : ''
        : String(centsToDollarsDecimal(proratedRentPaymentAmount)),
  });

  const [errors, setErrors] = useState({
    moveInCostsPaymentType: '',
    moveInCostsNotes: '',
    moveInCostsPaymentAmount: '',
  });

  const skipProratedRentPage = async (): Promise<any> => {
    setIsOpen(false);

    IreneOnboardingCSEvents.emitSkipProratedRentEvent({ currentProperty, user });

    await saveStateToReduxStore({
      proratedRentSettings: {
        proratedRentPaymentAmount: 0,
        proratedRentNotes: '',
        proratedRentStartsOpen: false,
      },
    });
  };

  const handleNotesChange = (e): void => {
    const key = e.target.dataset.statekey;
    let value = e.target.value;
    let error = '';

    if (!isMoveInCostsNoteLengthValid(value)) {
      value = value.substring(0, MOVE_IN_COSTS_NOTE_MAX_LENGTH);
      error = errorMessages.MOVE_IN_COSTS_NOTE_MAX_LENGTH;
    }

    setMoveInCosts((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: error }));
  };

  const handleAmountChange = (e): void => {
    e.preventDefault();
    const key = e.target.dataset.statekey;
    const value = e.target.value;
    const numericValue = removeNonDigit(value);

    setMoveInCosts((prev) => ({ ...prev, moveInCostsPaymentAmount: numericValue }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const handleSaveState = async (): Promise<any> => {
    const validInputs = validateProratedRentInputs({
      moveInCosts,
      individualLimit,
      handleErrors: (errors) => {
        setErrors(errors);
      },
    });

    if (!validInputs) {
      return;
    }

    const moveInCostsPaymentAmount = parseInt(dollarsToCents(moveInCosts.moveInCostsPaymentAmount), 10);

    if (totalPaymentAmount + moveInCostsPaymentAmount >= moveInCostsPaymentLimits) {
      await showModalNotification(MOVE_IN_COSTS_PAYMENT_LIMIT_ERROR);
      return;
    }

    IreneOnboardingCSEvents.emitContinueFromProratedRentEvent({
      currentProperty,
      user,
    });
    await saveStateToReduxStore({
      proratedRentSettings: {
        proratedRentPaymentAmount: moveInCostsPaymentAmount,
        proratedRentNotes: moveInCosts.moveInCostsNotes,
        proratedRentStartsOpen: isOpen,
      },
    });
  };

  const renderOpenedSection = (): JSX.Element => (
    <>
      <FormField
        control={
          <AdornedInput
            fluid
            error={Boolean(errors.moveInCostsPaymentAmount)}
            input={
              <AdornedInput.Input
                min="0"
                type="number"
                placeholder="Enter amount"
                className="fs-exclude input-caret-hide"
                value={!moveInCosts.moveInCostsPaymentAmount ? '' : moveInCosts.moveInCostsPaymentAmount}
                onChange={handleAmountChange}
                onBlur={handleAmountChange}
                onFocus={handleAmountChange}
                data-statekey="moveInCostsPaymentAmount"
              />
            }
            size="md"
            startAdornment={<AdornedInput.Adornment id="start-adornment">$</AdornedInput.Adornment>}
          />
        }
        description={errors.moveInCostsPaymentAmount && <FormHelp error>{errors.moveInCostsPaymentAmount}</FormHelp>}
        label={<Label>Amount</Label>}
      />

      <FormField
        control={
          <Input
            data-statekey="moveInCostsNotes"
            className="fs-exclude"
            placeholder="Enter any additional info"
            value={moveInCosts.moveInCostsNotes}
            onChange={handleNotesChange}
            onBlur={handleNotesChange}
          />
        }
        description={
          errors.moveInCostsNotes ? (
            <FormHelp error>{errors.moveInCostsNotes}</FormHelp>
          ) : (
            <FormHelp>{`${moveInCosts.moveInCostsNotes.length}/${MOVE_IN_COSTS_NOTE_MAX_LENGTH}`}</FormHelp>
          )
        }
        label={<Label>Note (optional)</Label>}
      />
    </>
  );

  const renderMainSection = (): JSX.Element => (
    <Box
      css={{
        display: 'grid',
        gridTemplateRows: 'auto',
        gap: 'token(spacing.sm) !important',
      }}
    >
      <Paragraph textStyle="body-lg-bold">Would you like to collect prorated rent?</Paragraph>
      <ToggleButtonGroup
        aria-label="prorated rent options"
        conjoined
        value={isOpen ? 'Yes' : ''}
        role="group"
        size="md"
      >
        <React.Fragment key=".0">
          <ToggleButton
            css={{ width: '100%' }}
            value="Yes"
            onClick={(): void => {
              setIsOpen(true);
            }}
          >
            Yes
          </ToggleButton>
          <ToggleButton css={{ width: '100%' }} value="No" onClick={skipProratedRentPage}>
            No
          </ToggleButton>
        </React.Fragment>
      </ToggleButtonGroup>
      {isOpen && renderOpenedSection()}
    </Box>
  );

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={onClose}
      header={
        <Box css={{ padding: 'token(spacing.sm) !important' }}>
          <TextButton textStyle="body-sm-bold" tone="brand" css={{ float: 'left' }} onClick={togglePrevPage}>
            Back
          </TextButton>
          <Heading level={3} textStyle="heading-xs" css={{ display: 'flex', justifyContent: 'center' }}>
            {fromDashboard ? 'Move-in costs' : 'Payment setup'}
          </Heading>
        </Box>
      }
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'token(spacing.sm) !important',
            padding: 'token(spacing.lg) !important',
          }}
        >
          {renderMainSection()}
          <FaqComponent faqs={FaqConstants.ONBOARDING_V2.MOVE_IN_COSTS.PRORATED_RENT_PAGE} />
        </Box>
      }
      footer={
        isOpen && (
          <Box css={{ padding: 'token(spacing.sm) !important' }}>
            <Button emphasis="filled" onClick={handleSaveState}>
              Continue
            </Button>
          </Box>
        )
      }
    />
  );
};

export default ProratedRentPage;
